/**
 * Card — one rendered card. Owner: Phase-3 A3.2 (gameplay interactions).
 *
 * Renders a pip card (suit + rank) or a joker (color glyph). Pure presentation —
 * legality, ordering, and selection logic live in `Hand`. Cards never compute
 * legality themselves (PRD FR-G3, ARCH §3.1: server is the oracle via
 * `view.legalMoves`).
 *
 * Phase-9 (v1.2): the inner face is now a single SVG `<use>` against the
 * hand-authored sprite at `/cards.svg`. The sprite contains 55 `<symbol>`
 * elements (52 pips/courts/aces + 2 jokers + 1 back) in viewBox 0 0 100 140.
 * The OUTER `.card` / `.card-pip` / `.card-joker` / `.card-suit-X` /
 * `.card-color-X` / `.card-trump` / `.card-dim` / `.card-interactive` class
 * hooks are unchanged — Hand.tsx's animation queries, Trick.tsx's
 * inline-style positioning, and the Playwright `.hand-slot.hand-slot-legal
 * button.card` selectors all keep working.
 *
 * Color-blind cue (PRD FR-U5): every pip card carries its rank + suit letter
 * baked into the SVG's corner indices (top-left + bottom-right rotated), so
 * the suit is legible regardless of red/green vision. Jokers are color-tagged
 * "RED"/"BLACK" in plain text inside the sprite's bottom panel.
 *
 * The `dim` prop is the legality cue used by `Hand` and `Trick` — a card that
 * is not in `view.legalMoves` (RULES §5.2, §5.4, §6 no-lead-joker, §3.2 sort)
 * renders dim and is non-interactive. The visual treatment is CSS-only; this
 * component never short-circuits the `onClick` itself — callers must omit the
 * handler when the card is dim. Tests pin both.
 *
 * Phase-4 A4.5 — `isTrump` (PRD FR-U2, RULES §3.2): Hand.tsx passes this
 * derived from `view.trump` so the player can see at a glance which cards in
 * their hand are the round's trump. Optional + defaults `false` so existing
 * callers (Trick.tsx, Hand.tsx pre-A4.5) keep working unchanged. The visual
 * treatment is CSS-only via the `card-trump` class; color-blind-safe (gold
 * top-edge stripe + small "★" mark next to the suit letter, not color-only).
 *
 * Phase-10 (v1.2) — the outer element is now `<m.button>` / `<m.div>` with
 * `layoutId={card.id}`. Motion's layout system tracks the same `layoutId`
 * across the React tree: when a card moves from Hand's JSX subtree into
 * Trick's subtree (the player plays a card), Motion auto-animates the
 * bounding-box transition. The same mechanism powers the sweep into the
 * WonPilesRail tile that fills when a trick is won. The engine-generated
 * `card.id` is stable across redactFor projections (see packages/engine/
 * src/deck.ts pipId()/jokerId()), so it's the canonical layout identity.
 * `MotionConfig reducedMotion="user"` in App.tsx snaps animations off when
 * the OS exposes prefers-reduced-motion — no per-component opt-out needed.
 */
import { useEffect, useRef } from "react";
import { m } from "motion/react";
import type { Card as ProtoCard, Suit } from "@hookum/protocol";

/** Animation spring for layout transitions (play, sweep, deal). Tuned to feel
 * "card-physical" — fast enough that the table doesn't stall, slow enough
 * that the eye tracks the card across the felt. */
const CARD_LAYOUT_TRANSITION = {
  type: "spring" as const,
  stiffness: 320,
  damping: 28,
  mass: 0.6,
};

/** Entrance animation — used the first time a card mounts (deal, or a card
 * appearing in the trick after a play). The card fades + scales in from
 * slightly above its final position. Motion's `initial` only fires on the
 * INITIAL mount; when a card transitions via `layoutId` between subtrees
 * (the play), Motion treats it as a continuous move and `initial` does NOT
 * re-fire. So this gives us a "deal" feel without fighting the play
 * transition. The `MotionConfig reducedMotion="user"` shell in App.tsx
 * snaps this off automatically when prefers-reduced-motion is on. */
const CARD_INITIAL = { opacity: 0, y: -18, scale: 0.92 };
const CARD_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const CARD_ENTRANCE_TRANSITION = {
  type: "spring" as const,
  stiffness: 360,
  damping: 24,
  mass: 0.5,
};

export interface CardProps {
  card: ProtoCard;
  dim?: boolean;
  isTrump?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  /**
   * Optional per-card delay (seconds) applied to the entrance animation
   * (opacity / y / scale). Used by Hand to stagger the deal so the player's
   * hand feels shuffled-and-dealt rather than spawning all at once.
   * Phase 11 (v1.2). Layout transitions (the play flight) ignore this delay
   * — only the entrance properties get it, so a card moving via layoutId
   * between subtrees still snaps responsively. Defaults to 0 (no stagger).
   */
  enterDelay?: number;
}

const SUIT_NAME: Record<Suit, string> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

function rankWord(rank: number): string {
  switch (rank) {
    case 11:
      return "Jack";
    case 12:
      return "Queen";
    case 13:
      return "King";
    case 14:
      return "Ace";
    default:
      return String(rank);
  }
}

function defaultAriaLabel(card: ProtoCard): string {
  if (card.kind === "joker") return `${card.color === "RED" ? "Red" : "Black"} Joker`;
  return `${rankWord(card.rank)} of ${SUIT_NAME[card.suit]}`;
}

/** Sprite symbol id — keep in sync with apps/web/scripts/gen-card-sprite.mjs. */
function spriteId(card: ProtoCard): string {
  if (card.kind === "joker") return card.color === "RED" ? "card-JR" : "card-JB";
  return `card-${card.suit}${card.rank}`;
}

export function Card({
  card,
  dim = false,
  isTrump = false,
  onClick,
  ariaLabel,
  enterDelay = 0,
}: CardProps): JSX.Element {
  const interactive = Boolean(onClick) && !dim;

  // v1.4 Phase 20.8 — gate the entrance animation (`initial={CARD_INITIAL}`)
  // to the FIRST render of this Card instance. Per-frame Playwright capture
  // showed that whenever ANY sibling Card with a layoutId was unmounted (the
  // played card moving from Hand to Trick), Motion re-fired `initial` on the
  // REMAINING hand cards — 10+ cards visibly dropped to opacity 0.019 and
  // y -18 px, then faded back in over ~500 ms. That's the "every time a card
  // is played my hand and the cards on the screen flicker" bug.
  //
  // We can't drop the entrance animation entirely — it's the deal-cascade
  // feature (Phase 11 v1.2). So instead we apply CARD_INITIAL only on the
  // first render (the deal); subsequent re-renders (because a sibling was
  // played, because legalMoves changed, etc.) get `initial={false}` so
  // Motion uses the current rendered state as the from-value (= no entrance
  // re-fire).
  //
  // v1.4.1 Phase 21.6 — the ref flip moved from render into a post-commit
  // useEffect. Mutating a ref during render is a StrictMode discarded-render
  // hazard: StrictMode invokes render twice, but only the second commit is
  // kept — the ref persists across the discarded first render, so on the
  // second render `hasRenderedRef.current` is already true and Phase 20.8's
  // first-mount entrance animation is silently skipped. Reading the ref in
  // render is fine (the value is correct); flipping it MUST happen after
  // commit so the discarded render leaves the ref untouched.
  const hasRenderedRef = useRef(false);
  const initialAnimate = hasRenderedRef.current ? false : CARD_INITIAL;
  useEffect(() => {
    hasRenderedRef.current = true;
  }, []);
  const baseClass = card.kind === "joker"
    ? `card card-joker card-joker-${card.color.toLowerCase()}`
    : `card card-pip card-suit-${card.suit.toLowerCase()}`;
  const color =
    card.kind === "joker"
      ? card.color === "RED"
        ? "red"
        : "black"
      : card.suit === "H" || card.suit === "D"
        ? "red"
        : "black";

  // Trump emphasis (PRD FR-U2): only applies to pip cards. Jokers are
  // ALWAYS strongest (RULES §5.3), so flagging them as "trump" would be
  // misleading — they win over trump too. Their distinct background is their
  // own cue.
  const trumpClass = isTrump && card.kind === "pip" ? " card-trump" : "";

  const className =
    `${baseClass} card-color-${color}` +
    trumpClass +
    (dim ? " card-dim" : "") +
    (interactive ? " card-interactive" : "");

  const label = ariaLabel ?? defaultAriaLabel(card);

  const commonProps = {
    className,
    "aria-label": label,
    "aria-disabled": dim ? true : undefined,
    "data-card-id": card.id,
    "data-dim": dim ? "true" : undefined,
    "data-trump": isTrump && card.kind === "pip" ? "true" : undefined,
  };

  // Phase 11 (v1.2): per-property transitions. Layout transitions (play,
  // sweep) use CARD_LAYOUT_TRANSITION unchanged; entrance properties use the
  // slightly snappier CARD_ENTRANCE_TRANSITION + per-card stagger delay so a
  // deal feels like a shuffled cascade. Motion v12's transition object
  // supports per-property keys + a `default` fallback.
  const entrance = { ...CARD_ENTRANCE_TRANSITION, delay: enterDelay };
  const cardTransition = {
    default: CARD_LAYOUT_TRANSITION,
    layout: CARD_LAYOUT_TRANSITION,
    opacity: entrance,
    y: entrance,
    scale: entrance,
  };

  const face = (
    <svg
      className="card-face"
      viewBox="0 0 100 140"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <use href={`/cards.svg#${spriteId(card)}`} />
    </svg>
  );

  if (interactive) {
    return (
      <m.button
        type="button"
        onClick={onClick}
        layoutId={card.id}
        layout="position"
        initial={initialAnimate}
        animate={CARD_ANIMATE}
        transition={cardTransition}
        {...commonProps}
      >
        {face}
      </m.button>
    );
  }
  return (
    <m.div
      role="img"
      layoutId={card.id}
      layout="position"
      initial={initialAnimate}
      animate={CARD_ANIMATE}
      transition={CARD_LAYOUT_TRANSITION}
      {...commonProps}
    >
      {face}
    </m.div>
  );
}
